import type { MedplumClient } from "@medplum/core";
import type {
  Encounter,
  Observation,
  Patient,
  Practitioner,
  QuestionnaireResponse,
  Reference,
} from "@medplum/fhirtypes";

import { LOINCCodes } from "@/fhir/enums/loinc";
import { Systems } from "@/fhir/enums/systems";
import { joinTokenString } from "@/utils/medplum";

/** Parameters for building a cumulative blood loss Observation (shared by SPA and encounter bots). */
export interface CreateCumulativeBloodLossObservationParams {
  patient: Reference<Patient>;
  practitioner: Reference<Practitioner>;
  encounter: Reference<Encounter>;
  cumulativeValue: number;
  laborStageAnswerCode: string;
  laborStageAnswerDisplay: string;
  questionnaireResponse?: Reference<QuestionnaireResponse>;
  compartment?: Reference<Patient>[];
  effectiveDateTime?: string;
  issued?: string;
}

/**
 * Builds a final cumulative blood loss Observation resource body (LOINC, category, components).
 * Callers supply the labor-stage answer code and display so UI and bots can use their own stage maps.
 */
export function createCumulativeBloodLossObservation(
  params: CreateCumulativeBloodLossObservationParams,
): Observation {
  const {
    patient,
    practitioner,
    encounter,
    cumulativeValue,
    laborStageAnswerCode,
    laborStageAnswerDisplay,
    questionnaireResponse,
    compartment = [],
    effectiveDateTime = new Date().toISOString(),
    issued = new Date().toISOString(),
  } = params;

  return {
    resourceType: "Observation",
    status: "final",
    category: [
      {
        coding: [
          {
            system: Systems.HL7ObservationCategory,
            code: "vital-signs",
            display: "Vital Signs",
          },
        ],
        text: "Vital Signs",
      },
    ],
    code: {
      coding: [
        {
          system: Systems.LOINC,
          code: LOINCCodes.CumulativeBloodLoss,
          display: "Cumulative Blood Loss",
        },
      ],
      text: "Cumulative Blood Loss",
    },
    subject: patient,
    encounter,
    performer: [practitioner],
    effectiveDateTime,
    issued,
    valueQuantity: {
      value: cumulativeValue,
      unit: "cc",
      system: Systems.UOM,
      code: "cc",
    },
    meta: {
      compartment,
    },
    component: [
      {
        code: {
          coding: [
            {
              system: Systems.LOINC,
              code: LOINCCodes.LaborStage,
              display: "Labor stage [Type]",
            },
          ],
        },
        valueCodeableConcept: {
          coding: [
            {
              system: Systems.LOINC,
              code: laborStageAnswerCode,
              display: laborStageAnswerDisplay,
            },
          ],
        },
      },
    ],
    ...(questionnaireResponse && {
      derivedFrom: [questionnaireResponse],
      hasMember: [questionnaireResponse],
    }),
  };
}

/**
 * Returns the latest final cumulative blood loss observation for the patient on this encounter.
 * Shared by encounter UI and encounter bots (avoid importing from presentation-layer paths).
 */
export async function getCumulativeBloodLossObservation(
  medplum: MedplumClient,
  patient: Reference<Patient>,
  encounter: Reference<Encounter>,
): Promise<Observation | null> {
  return (
    (await medplum.searchOne("Observation", {
      subject: patient.reference,
      encounter: encounter.reference,
      code: joinTokenString(Systems.LOINC, LOINCCodes.CumulativeBloodLoss),
      status: "final",
      _sort: "-_lastUpdated",
      _count: 1,
    })) ?? null
  );
}
